import React from 'react';
import GlobalProps from '../../../../components/globalProps';
import {
  MerchProductVariant,
  Product,
  createVariant,
  updateVariant,
  getProductSaleStats,
} from '../../../../model/product';
import { ModalController } from '../../../../components/Modal/ControlledModal';
import { TextField } from '../../../../components/Form/TextField/TextField';
import { Label } from '../../../../components/Form/Label/Label';
import { RadioGroup } from '../../../../components/Form/Radio/RadioGroup';
import { Datepicker } from '../../../../components/Form/Datepicker/Datepicker';
import { handleError } from '../../../../model/notification';
import { useChanges, Form } from '../../../../components/Form/Form';
import { t } from '../../../../locales/translate';
import './ProductVariantsForm.css';
import { Checkbox } from '../../../../components/Form/Checkbox/Checkbox';
import { Stack } from '../../../../components/Stack/Stack';
import { Spacer } from '../../../../components/Spacer/Spacer';
import { identifyBarcode } from '../../../../util/barcode';

interface Props extends GlobalProps {
  controller: ModalController<Partial<MerchProductVariant>>;
  product: Product;
}

export function ProductVariantsForm({ product, controller }: Props): JSX.Element {
  const newVariant = Object.keys(controller.model).length === 0;
  function getVariantActiveType(): 'inactive' | 'now' | 'activeFrom' {
    if (newVariant) {
      return 'inactive';
    } else {
      if (controller.model.active) {
        return 'now';
      } else {
        const today = new Date();
        const future = !controller.model.activeFrom || controller.model.activeFrom > today;
        if (future) {
          return 'activeFrom';
        } else {
          return 'inactive';
        }
      }
    }
  }

  const [changes, , model, trackChanges] = useChanges<
    Partial<MerchProductVariant> & {
      activeType?: 'now' | 'inactive' | 'activeFrom';
    }
  >({
    ...controller.model,
    activeType: getVariantActiveType(),
  });

  function calculateActiveUntil(): Date | null | undefined {
    const changedActiveUntil = Object.keys(changes).includes('activeUntil');
    const deleteActiveUntil = changedActiveUntil && changes.activeUntil === undefined;
    return deleteActiveUntil ? null : changes.activeUntil ? changes.activeUntil : controller.model.activeUntil;
  }

  function parseActiveType(activeType?: 'now' | 'inactive' | 'activeFrom'):
    | {
        activeFrom?: Date | null;
        activeUntil?: Date | null;
      }
    | undefined {
    const timestamp = new Date();
    timestamp.setHours(timestamp.getHours() - 1);

    switch (activeType) {
      case 'now':
        return {
          activeFrom: timestamp,
          activeUntil: null,
        };
      case 'inactive':
        return {
          activeUntil: timestamp,
        };
      case 'activeFrom':
        return {
          activeFrom: changes.activeFrom,
          activeUntil: calculateActiveUntil(),
        };
      default:
        return undefined;
    }
  }

  async function handleOnSubmit(): Promise<void> {
    try {
      if (newVariant) {
        await createVariant({
          productId: product.id,
          changes: {
            ...changes,
            ...parseActiveType(changes.activeType || getVariantActiveType()),
          },
        });
        getProductSaleStats(product.id);
      } else {
        if (Object.keys(changes).length > 0 && controller.model.id) {
          await updateVariant({
            productId: product.id,
            productVariantId: controller.model.id,
            changes: {
              ...changes,
              ...(parseActiveType(changes.activeType) || {
                activeFrom: changes.activeFrom,
                activeUntil: changes.activeUntil,
              }),
            },
          });
        }
      }
      controller.close();
    } catch (error) {
      handleError(error);
    }
  }

  const [validEanMessage, invalidEanMessage] = eanMessages(model.ean);

  return (
    <div className="product-variants-form">
      <Form model={model} onInput={trackChanges}>
        {(form) => {
          controller.configure({
            onSubmit: handleOnSubmit,
            canSubmit: form.valid,
          });
          return (
            <Stack>
              <TextField name="description" label={t.model.product.variation} />
              <TextField name="weight" label={t.model.product.weight} type="number" />
              <TextField name="minimumStockCount" type="number" label={t.model.product.minAvailability} />
              <TextField
                name="ean"
                label={t.model.productVariant.ean}
                warningMessage={invalidEanMessage}
                successMessage={validEanMessage}
              />
              <TextField name="legacyId" label={t.general.legacyId} />
              <Checkbox name="private" label={t.model.productVariant.private} />
              <Spacer size="none" />
              <Label title={t.model.product.productVariantActivation} />
              <RadioGroup
                name="activeType"
                values={[
                  { label: t.model.status.activeNow, value: 'now' },
                  { label: t.model.status.inactive, value: 'inactive' },
                  { label: t.model.status.scheduled, value: 'activeFrom' },
                ]}
              />
              {model.activeType === 'activeFrom' && (
                <>
                  <Datepicker
                    name="activeFrom"
                    hourpicker
                    label={t.model.datetime.activeDate}
                    disabledDays={[{ before: new Date() }]}
                    required
                  />
                  <Datepicker
                    name="activeUntil"
                    hourpicker
                    label={t.model.product.deactivateAt}
                    disabledDays={[{ before: new Date() }]}
                  />
                </>
              )}
            </Stack>
          );
        }}
      </Form>
    </div>
  );
}

function eanMessages(ean: string | undefined): [string | undefined, string | undefined] {
  if ((ean?.length ?? 0) <= 0) {
    return [undefined, undefined];
  }
  const barcodeType = identifyBarcode(ean);
  switch (barcodeType) {
    case 'ean-13':
      return [t.model.productVariant.eanValid, undefined];
    case 'upc-a':
      return [t.model.productVariant.upcValid, undefined];
    default:
      return [undefined, t.model.productVariant.eanInvalid];
  }
}
